import { AnimatePresence, motion } from 'motion/react';
import { Compass, MapPin, Sparkles } from 'lucide-react';
import { useTranslation } from '../hooks/useTranslation';
import SouthAfricaGold from '../assets/SouthAfricaGold.png';
import { useState } from 'react';

interface SafariHotspot {
	id: string;
	name: string;
	region: string;
	x: number;
	y: number;
	badge: string;
}

const HOTSPOTS: SafariHotspot[] = [
	{
		id: 'kruger',
		name: 'Greater Kruger & Sabi Sand',
		region: 'Limpopo / Mpumalanga',
		x: 272,
		y: 75,
		badge: 'Big 5 Flagship',
	},
	{
		id: 'zululand',
		name: 'Zululand & Hluhluwe',
		region: 'KwaZulu-Natal',
		x: 280,
		y: 135,
		badge: 'Rhino & Elephant Sanctuary',
	},
	{
		id: 'madikwe',
		name: 'Madikwe Wilderness',
		region: 'North West',
		x: 182,
		y: 82,
		badge: 'Malaria-Free Safari',
	},
	{
		id: 'capetown',
		name: 'Cape Town & Winelands',
		region: 'Western Cape',
		x: 52,
		y: 236,
		badge: 'Atlantic Coast & Lodges',
	},
	{
		id: 'gardenroute',
		name: 'Garden Route',
		region: 'Southern Cape',
		x: 125,
		y: 248,
		badge: 'Scenic Coastal Escapes',
	},
];
export default function SouthAfricaMap() {
	const { t } = useTranslation();
	const [activeHotspot, setActiveHotspot] = useState<SafariHotspot | null>(null);

	return (
		<motion.div
			initial={{ opacity: 0, x: 20 }}
			animate={{ opacity: 1, x: 0 }}
			transition={{ duration: 1, delay: 0.6 }}
			/* 1. Increased width: w-80 (320px) or sm:w-96 (384px) */
			className='bg-white/5 border border-white/10 p-6 rounded-xl backdrop-blur-md text-left w-80 sm:w-96'
		>
			{/* Header Title */}
			<div className='w-full flex items-center justify-between pb-3 mb-2 border-b border-white/10'>
				<div className='flex items-center space-x-2'>
					<Compass className='w-4 h-4 text-brand-gold animate-spin-slow' />
					<span className='font-serif text-sm text-earth-cream font-medium tracking-wide'>
						{t('discoverSouthAfrica')}
					</span>
				</div>
			</div>

			{/* South Africa Map */}
			<div className='relative w-full aspect-4/3 my-2 flex items-center justify-center'>
				<img
					src={SouthAfricaGold}
					alt='South Africa Map'
					className='object-contain w-full h-full'
					referrerPolicy='no-referrer'
				/>
				<svg
					viewBox='0 0 320 280'
					className='absolute inset-0 w-full h-full filter drop-shadow-md select-none pointer-events-auto z-10'
					fill='none'
					xmlns='http://www.w3.org/2000/svg'
				>
					<defs>
						<filter id='goldGlow' x='-50%' y='-50%' width='200%' height='200%'>
							<feGaussianBlur in='SourceGraphic' stdDeviation='2' result='blur' />
							<feMerge>
								<feMergeNode in='blur' />
								<feMergeNode in='SourceGraphic' />
							</feMerge>
						</filter>
					</defs>

					{/* Ocean Accents with larger font */}
					<text
						x='230'
						y='260'
						fill='#CDA250'
						opacity='0.45'
						fontSize='11'
						fontWeight='600'
						fontFamily='sans-serif'
						letterSpacing='1.5'
						className='pointer-events-none'
					>
						INDIAN OCEAN
					</text>

					{/* Optional: Atlantic Ocean if you want both balanced */}
					<text
						x='-25'
						y='260'
						fill='#CDA250'
						opacity='0.45'
						fontSize='10'
						fontWeight='600'
						fontFamily='sans-serif'
						letterSpacing='1.5'
						className='pointer-events-none'
					>
						ATLANTIC
					</text>

					{HOTSPOTS.map(spot => {
						const isHovered = activeHotspot?.id === spot.id;
						return (
							<g
								key={spot.id}
								className='cursor-pointer group'
								onMouseEnter={() => setActiveHotspot(spot)}
								onMouseLeave={() => setActiveHotspot(null)}
								onClick={() => setActiveHotspot(spot)}
							>
								<circle
									cx={spot.x}
									cy={spot.y}
									r={isHovered ? '9' : '6'}
									stroke='#1E3A27'
									strokeWidth='1'
									opacity={isHovered ? '0.8' : '0.4'}
									className='animate-ping'
								/>
								<circle
									cx={spot.x}
									cy={spot.y}
									r={isHovered ? '5' : '3.5'}
									fill='#122419'
									stroke='#0F2C24'
									strokeWidth='1.2'
									filter='url(#goldGlow)'
								/>
							</g>
						);
					})}
				</svg>
			</div>

			{/* Dynamic Hotspot Information Card */}
			<div className='h-14 pt-1 flex items-center'>
				<AnimatePresence mode='wait'>
					{activeHotspot ? (
						<motion.div
							key={activeHotspot.id}
							initial={{ opacity: 0, y: 4 }}
							animate={{ opacity: 1, y: 0 }}
							exit={{ opacity: 0, y: -4 }}
							className='w-full bg-brand-gold/15 border border-brand-gold/30 rounded-lg p-2.5 flex items-center justify-between text-left gap-2'
						>
							<div className='min-w-0 flex-1'>
								<div className='flex items-center space-x-1.5'>
									<Sparkles className='w-3.5 h-3.5 text-brand-gold shrink-0' />
									<p className='font-serif text-xs font-semibold text-earth-cream leading-tight truncate'>
										{activeHotspot.name}
									</p>
								</div>
								<p className='font-sans text-[10px] text-brand-gold-light mt-0.5 truncate'>{activeHotspot.badge}</p>
							</div>
							{/* Region tag with max width & text alignment */}
							<span className='font-mono text-[9px] text-earth-sand-light uppercase tracking-wider text-right shrink-0 max-w-30 leading-tight'>
								{activeHotspot.region}
							</span>
						</motion.div>
					) : (
						<motion.div
							key='default-footer'
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							exit={{ opacity: 0 }}
							className='w-full pt-2 flex items-center justify-between text-earth-sand-light text-[11px] font-sans'
						>
							<div className='flex items-center space-x-1.5'>
								<MapPin className='w-3.5 h-3.5 text-brand-gold' />
								<span className='font-medium text-white/90'>South Africa</span>
							</div>
							<span className='text-[10px] text-brand-gold tracking-wider uppercase font-semibold'>
								Premier Safari Hub
							</span>
						</motion.div>
					)}
				</AnimatePresence>
			</div>
		</motion.div>
	);
}
